import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import { Button } from '@rekrytering/ui/components/button'
import { Input } from '@rekrytering/ui/components/input'
import { useForm } from '@tanstack/react-form'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Upload } from 'lucide-react'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { ContactSupportForm } from '@/features/settings/components/contact-support-form'
import { useDoubleCheck } from '@/shared/hooks/use-double-check'
import { useSignOut } from '@/shared/hooks/use-sign-out'
import { Image } from '@/shared/ui/image'
import * as validators from '@/shared/utils/validators'

export function SettingsPage() {
  const { t } = useTranslation()
  const { data: user } = useQuery(
    convexQuery(api.features.user.user.getMyCurrentUser, {}),
  )
  const signOut = useSignOut()
  const { mutateAsync: updateProfile } = useMutation({
    mutationFn: useConvexMutation(api.features.user.user.updateMyProfile),
  })
  const { mutateAsync: updateUsername } = useMutation({
    mutationFn: useConvexMutation(api.features.user.user.updateMyUsername),
  })
  const { mutateAsync: updateUserImage } = useMutation({
    mutationFn: useConvexMutation(api.features.user.user.updateMyUserImage),
  })
  const { mutateAsync: removeUserImage } = useMutation({
    mutationFn: useConvexMutation(api.features.user.user.removeMyUserImage),
  })
  const { mutateAsync: deleteCurrentUserAccount } = useMutation({
    mutationFn: useConvexMutation(api.features.user.user.deleteMyAccount),
  })
  const generateUploadUrl = useConvexMutation(
    api.features.user.user.generateMyUploadUrl,
  )
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileUpload = async (file: File) => {
    try {
      const uploadUrl = await generateUploadUrl({})
      const result = await fetch(uploadUrl, {
        method: 'POST',
        headers: { 'Content-Type': file.type },
        body: file,
      })
      if (!result.ok) throw new Error('Upload failed')
      const { storageId } = await result.json()
      await updateUserImage({ imageId: storageId })
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }
  const { doubleCheck, getButtonProps } = useDoubleCheck()

  const profileForm = useForm({
    defaultValues: {
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
    },
    onSubmit: async ({ value }) => {
      await updateProfile({
        firstName: value.firstName || undefined,
        lastName: value.lastName || undefined,
      })
    },
  })

  const usernameForm = useForm({
    defaultValues: {
      username: user?.username,
    },
    onSubmit: async ({ value }) => {
      await updateUsername({ username: value.username || '' })
    },
  })

  const handleDeleteAccount = async () => {
    await deleteCurrentUserAccount({})
    signOut()
  }

  if (!user) {
    return null
  }

  return (
    <div className="flex h-full w-full flex-col gap-6">
      <div className="flex w-full flex-col items-start rounded-lg border border-border bg-card">
        <div className="flex w-full items-start justify-between rounded-lg p-6">
          <div className="flex flex-col gap-2">
            <h2 className="text-xl font-medium text-primary">
              {t('settings.avatar.title')}
            </h2>
            <p className="text-sm font-normal text-primary/60">
              {t('settings.avatar.description')}
            </p>
          </div>
          <label
            htmlFor="avatar_field"
            className="group relative flex cursor-pointer overflow-hidden rounded-full transition active:scale-95"
          >
            {user.avatarUrl ? (
              <Image
                src={user.avatarUrl}
                className="h-20 w-20 rounded-full object-cover"
                alt={user.username ?? user.email}
                width={80}
                height={80}
              />
            ) : (
              <div className="h-20 w-20 rounded-full bg-linear-to-br from-lime-400 from-10% via-cyan-300 to-blue-500" />
            )}
            <div className="absolute z-10 hidden h-full w-full items-center justify-center bg-primary/40 group-hover:flex">
              <Upload className="h-6 w-6 text-secondary" />
            </div>
          </label>
          <input
            ref={fileInputRef}
            id="avatar_field"
            type="file"
            accept="image/*"
            className="peer sr-only"
            required
            tabIndex={user ? -1 : 0}
            onChange={async (event) => {
              const file = event.target.files?.[0]
              if (!file) return
              await handleFileUpload(file)
            }}
          />
        </div>
        <div className="flex min-h-14 w-full items-center justify-between rounded-lg rounded-t-none border-t border-border bg-secondary px-6 dark:bg-card">
          <p className="text-sm font-normal text-primary/60">
            {t('settings.avatar.hint')}
          </p>
          {user.avatarUrl && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => {
                removeUserImage({})
              }}
            >
              {t('settings.avatar.reset')}
            </Button>
          )}
        </div>
      </div>

      <form
        className="flex w-full flex-col items-start rounded-lg border border-border bg-card"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          profileForm.handleSubmit()
        }}
      >
        <div className="flex w-full flex-col gap-4 rounded-lg p-6">
          <div className="flex flex-col gap-2">
            <h2 className="text-xl font-medium text-primary">
              {t('settings.profile.title')}
            </h2>
            <p className="text-sm font-normal text-primary/60">
              {t('settings.profile.description')}
            </p>
          </div>
          <div className="flex gap-3">
            <profileForm.Field
              name="firstName"
              children={(field) => (
                <Input
                  placeholder={t('settings.profile.firstNamePlaceholder')}
                  autoComplete="given-name"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className="w-40 bg-transparent"
                />
              )}
            />
            <profileForm.Field
              name="lastName"
              children={(field) => (
                <Input
                  placeholder={t('settings.profile.lastNamePlaceholder')}
                  autoComplete="family-name"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className="w-40 bg-transparent"
                />
              )}
            />
          </div>
        </div>
        <div className="flex min-h-14 w-full items-center justify-between rounded-lg rounded-t-none border-t border-border bg-secondary px-6 dark:bg-card">
          <p className="text-sm font-normal text-primary/60">
            {t('settings.profile.hint')}
          </p>
          <Button type="submit" size="sm">
            {t('settings.profile.save')}
          </Button>
        </div>
      </form>

      <form
        className="flex w-full flex-col items-start rounded-lg border border-border bg-card"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          usernameForm.handleSubmit()
        }}
      >
        <div className="flex w-full flex-col gap-4 rounded-lg p-6">
          <div className="flex flex-col gap-2">
            <h2 className="text-xl font-medium text-primary">
              {t('settings.username.title')}
            </h2>
            <p className="text-sm font-normal text-primary/60">
              {t('settings.username.description')}
            </p>
          </div>
          <usernameForm.Field
            name="username"
            validators={{
              onSubmit: validators.username,
            }}
            children={(field) => (
              <Input
                placeholder={t('settings.username.placeholder')}
                autoComplete="off"
                required
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                className={`w-80 bg-transparent ${
                  field.state.meta?.errors.length > 0 &&
                  'border-destructive focus-visible:ring-destructive'
                }`}
              />
            )}
          />
          {(usernameForm.state.fieldMeta.username?.errors?.length ?? 0) > 0 && (
            <p className="text-sm text-destructive dark:text-destructive-foreground">
              {usernameForm.state.fieldMeta.username?.errors.join(' ')}
            </p>
          )}
        </div>
        <div className="flex min-h-14 w-full items-center justify-between rounded-lg rounded-t-none border-t border-border bg-secondary px-6 dark:bg-card">
          <p className="text-sm font-normal text-primary/60">
            {t('settings.username.hint')}
          </p>
          <Button type="submit" size="sm">
            {t('settings.username.save')}
          </Button>
        </div>
      </form>

      <ContactSupportForm />

      <div className="flex w-full flex-col items-start rounded-lg border border-destructive bg-card">
        <div className="flex flex-col gap-2 p-6">
          <h2 className="text-xl font-medium text-primary">
            {t('settings.deleteAccount.title')}
          </h2>
          <p className="text-sm font-normal text-primary/60">
            {t('settings.deleteAccount.description')}
          </p>
        </div>
        <div className="flex min-h-14 w-full items-center justify-between rounded-lg rounded-t-none border-t border-border bg-red-500/10 px-6 dark:bg-red-500/10">
          <p className="text-sm font-normal text-primary/60">
            {t('settings.deleteAccount.hint')}
          </p>
          <Button
            size="sm"
            variant="destructive"
            {...getButtonProps({
              onClick: doubleCheck ? handleDeleteAccount : undefined,
            })}
          >
            {doubleCheck
              ? t('settings.deleteAccount.confirm')
              : t('settings.deleteAccount.delete')}
          </Button>
        </div>
      </div>
    </div>
  )
}
